(() => {
  class CicCollectionQuickSearchController {
    constructor(root) {
      this.root = root;
      this.onSubmit = this.onSubmit.bind(this);
      this.onClear = this.onClear.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onInputFocus = this.onInputFocus.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onGridMutation = this.onGridMutation.bind(this);
      this.onGridClick = this.onGridClick.bind(this);

      this.lastSubmitAt = 0;
      this.scopeCache = new Map();
      this.searchRequestId = 0;
      this.isClientRendering = false;
      this.ignoreMutationsUntil = 0;
      this.lastAppliedTerm = '';
      this.active = false;
      this.scopePrimed = false;
      this.loadingNoticeText = 'Searching this collection...';
    }

    connect() {
      if (this.active) return;

      this.form = this.root.querySelector('.cic-collection-quick-search__form');
      this.input = this.root.querySelector('.cic-collection-quick-search__input');
      this.clearButton = this.root.querySelector('.cic-collection-quick-search__clear');
      this.notice = this.root.querySelector('.cic-collection-quick-search__notice');
      this.productGridContainer = document.getElementById('ProductGridContainer');

      if (!this.form || !this.input) return;

      const initialGrid = document.querySelector('#product-grid');
      const initialCollection = initialGrid ? initialGrid.closest('.collection') : null;
      this.initialGridClassName = initialGrid ? initialGrid.className : '';
      this.initialCollectionClassName = initialCollection ? initialCollection.className : 'collection page-width';
      this.initialPageSize = initialGrid ? initialGrid.querySelectorAll('.grid__item').length : 0;

      this.form.addEventListener('submit', this.onSubmit);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onInputFocus, { once: true });
      if (this.clearButton) this.clearButton.addEventListener('click', this.onClear);
      window.addEventListener('popstate', this.onPopState);

      if (this.productGridContainer) {
        this.gridObserver = new MutationObserver(this.onGridMutation);
        this.gridObserver.observe(this.productGridContainer, { childList: true, subtree: true });
        this.productGridContainer.addEventListener('click', this.onGridClick);
      }

      this.syncFromUrl();
      this.toggleClearButton();
      this.applyDeferredProductCountFocus();
      this.applySearchFromUrl({ focusProductCount: false });
      this.active = true;
    }

    disconnect() {
      if (!this.active) return;
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.input) this.input.removeEventListener('input', this.onInput);
      if (this.input) this.input.removeEventListener('focus', this.onInputFocus);
      if (this.clearButton) this.clearButton.removeEventListener('click', this.onClear);
      window.removeEventListener('popstate', this.onPopState);

      if (this.gridObserver) {
        this.gridObserver.disconnect();
        this.gridObserver = null;
      }

      if (this.productGridContainer) {
        this.productGridContainer.removeEventListener('click', this.onGridClick);
      }

      if (this.mutationTimeout) {
        clearTimeout(this.mutationTimeout);
        this.mutationTimeout = null;
      }

      this.active = false;
    }

    onInput() {
      this.toggleClearButton();
    }

    onInputFocus() {
      this.primeScopeCache();
    }

    onPopState() {
      this.syncFromUrl();
      this.toggleClearButton();
      this.applySearchFromUrl({ focusProductCount: false });
    }

    onGridMutation() {
      if (this.isClientRendering) return;
      if (Date.now() < this.ignoreMutationsUntil) return;

      if (this.mutationTimeout) clearTimeout(this.mutationTimeout);
      this.mutationTimeout = setTimeout(() => {
        this.syncFromUrl();
        this.applySearchFromUrl({ focusProductCount: false });
      }, 80);
    }

    onGridClick(event) {
      const link = event.target.closest('a[data-cic-page]');
      if (!link) return;

      event.preventDefault();
      const href = link.getAttribute('href');
      if (!href) return;

      const query = href.includes('?') ? href.slice(href.indexOf('?') + 1) : '';
      history.pushState({ searchParams: query }, '', href);
      this.syncFromUrl();
      this.applySearchFromUrl({ focusProductCount: true });
    }

    onClear() {
      if (!this.input) return;
      this.input.value = '';
      this.toggleClearButton();
      this.form.requestSubmit();
    }

    async onSubmit(event) {
      event.preventDefault();

      const now = Date.now();
      if (now - this.lastSubmitAt < 500) return;
      this.lastSubmitAt = now;

      const rawValue = this.input.value.trim();
      const params = new URLSearchParams(window.location.search);

      params.delete('cic_q_mode');
      params.delete('cic_q_display');

      if (!rawValue) {
        params.delete('q');
        params.delete('options[prefix]');
        params.delete('page');
        this.pushUrl(params);
        this.setBusyState(true);
        await this.applySearchFromUrl({ focusProductCount: true });
        this.setBusyState(false);
        return;
      }

      this.removeFilterParams(params);
      params.delete('page');
      params.set('sort_by', 'relevance');
      params.set('options[prefix]', 'last');
      params.set('q', rawValue);
      params.set('cic_q_display', rawValue);
      this.pushUrl(params);
      this.applyFastPreview(rawValue);
      this.setBusyState(true);
      await this.applySearchFromUrl({ focusProductCount: true });
      this.setBusyState(false);
    }

    primeScopeCache() {
      if (this.scopePrimed) return;
      this.scopePrimed = true;
      const params = new URLSearchParams(window.location.search);
      const scopeParams = this.getScopeParams(params);
      this.loadScopeCards(scopeParams).catch(() => {});
    }

    setBusyState(isBusy) {
      if (!this.form) return;
      this.form.classList.toggle('is-searching', Boolean(isBusy));
      const submit = this.form.querySelector('.cic-collection-quick-search__submit');
      if (submit) {
        submit.disabled = Boolean(isBusy);
        submit.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }

      if (!this.notice) return;
      if (isBusy) {
        this.notice.hidden = false;
        this.notice.textContent = this.loadingNoticeText;
      } else if (this.notice.textContent === this.loadingNoticeText) {
        this.notice.hidden = true;
        this.notice.textContent = '';
      }
    }

    pushUrl(params, replace = false) {
      const queryString = params.toString();
      const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
      if (replace) {
        history.replaceState({ searchParams: queryString }, '', nextUrl);
      } else {
        history.pushState({ searchParams: queryString }, '', nextUrl);
      }
    }

    removeFilterParams(params) {
      const keys = Array.from(params.keys());
      keys.forEach((key) => {
        if (key.startsWith('filter.')) params.delete(key);
      });
    }

    syncFromUrl() {
      if (!this.input) return;
      const params = new URLSearchParams(window.location.search);
      const displayValue = params.get('cic_q_display');
      const queryValue = params.get('q');
      const hasDisplayValue = displayValue !== null && displayValue !== '';
      this.input.value = hasDisplayValue ? displayValue : (queryValue || '');
    }

    applyModeNotice(mode = '') {
      if (!this.notice) return;

      if (mode === 'keyword_fallback') {
        this.notice.hidden = false;
        this.notice.textContent = this.root.dataset.fallbackMessage || 'No exact match; showing keyword matches.';
        return;
      }

      this.notice.hidden = true;
      this.notice.textContent = '';
    }

    applyDeferredProductCountFocus() {
      if (sessionStorage.getItem('cic_focus_product_count') !== '1') return;
      sessionStorage.removeItem('cic_focus_product_count');
      this.focusProductCount();
    }

    focusProductCount() {
      const productCount = document.getElementById('ProductCount');
      if (!productCount) return;
      productCount.setAttribute('tabindex', '-1');
      productCount.focus({ preventScroll: false });
    }

    async applySearchFromUrl({ focusProductCount = false } = {}) {
      const params = new URLSearchParams(window.location.search);
      const term = (params.get('cic_q_display') || params.get('q') || '').trim();

      if (!term && !this.lastAppliedTerm) {
        this.applyModeNotice('');
        this.writeModeToUrl('', '');
        this.applyMatchedSkuNotes();
        if (focusProductCount) this.focusProductCount();
        return;
      }

      const requestId = ++this.searchRequestId;
      const scopeParams = this.getScopeParams(params);
      const scopeKey = scopeParams.toString();
      const hasCachedScope = this.scopeCache.has(scopeKey);
      if (term && !hasCachedScope) {
        this.applyFastPreview(term);
      }
      const cards = await this.loadScopeCards(scopeParams);
      if (requestId !== this.searchRequestId) return;

      if (!term) {
        if (this.lastAppliedTerm) {
          this.renderFilteredGrid(cards);
          this.updateProductCount(cards.length);
        }
        this.lastAppliedTerm = '';
        this.applyModeNotice('');
        this.writeModeToUrl('', '');
        this.applyMatchedSkuNotes();
        if (focusProductCount) this.focusProductCount();
        return;
      }

      const result = this.filterCards(cards, term);
      this.lastAppliedTerm = term;
      this.writeModeToUrl(result.mode, term);
      this.applyModeNotice(result.mode);
      this.renderFilteredGrid(result.cards);
      this.updateProductCount(result.cards.length);
      this.applyMatchedSkuNotes();

      if (focusProductCount) this.focusProductCount();
    }

    getScopeParams(params) {
      const scopeParams = new URLSearchParams(params.toString());
      scopeParams.delete('q');
      scopeParams.delete('options[prefix]');
      scopeParams.delete('cic_q_mode');
      scopeParams.delete('cic_q_display');
      scopeParams.delete('page');
      scopeParams.delete('section_id');
      return scopeParams;
    }

    async loadScopeCards(scopeParams) {
      const cacheKey = scopeParams.toString();
      if (!this.scopeCache.has(cacheKey)) {
        this.scopeCache.set(cacheKey, this.fetchAllScopeCards(scopeParams));
      }

      try {
        return await this.scopeCache.get(cacheKey);
      } catch (error) {
        this.scopeCache.delete(cacheKey);
        return [];
      }
    }

    async fetchAllScopeCards(scopeParams) {
      const firstDoc = await this.fetchScopePage(scopeParams, 1);
      if (!firstDoc) return [];

      const pageResults = new Map();
      pageResults.set(1, this.extractCardsFromDocument(firstDoc));
      const totalPages = this.getMaxPageFromDocument(firstDoc, 1);
      if (totalPages <= 1) {
        return pageResults.get(1);
      }

      const maxPages = Math.min(totalPages, 120);
      const pages = [];
      for (let page = 2; page <= maxPages; page += 1) {
        pages.push(page);
      }

      const concurrency = 6;
      for (let start = 0; start < pages.length; start += concurrency) {
        const batch = pages.slice(start, start + concurrency);
        const docs = await Promise.all(batch.map((page) => this.fetchScopePage(scopeParams, page)));
        docs.forEach((doc, index) => {
          if (!doc) return;
          const page = batch[index];
          pageResults.set(page, this.extractCardsFromDocument(doc));
        });
      }

      const cards = [];
      for (let page = 1; page <= maxPages; page += 1) {
        const pageCards = pageResults.get(page);
        if (pageCards && pageCards.length) cards.push(...pageCards);
      }
      return cards;
    }

    async fetchScopePage(scopeParams, page) {
      const sectionId = this.root.dataset.sectionId;
      if (!sectionId) return null;

      const params = new URLSearchParams(scopeParams.toString());
      params.set('section_id', sectionId);
      if (page > 1) {
        params.set('page', String(page));
      } else {
        params.delete('page');
      }

      const url = `${window.location.pathname}?${params.toString()}`;

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        if (!response.ok) return null;
        const html = await response.text();
        return new DOMParser().parseFromString(html, 'text/html');
      } catch (error) {
        return null;
      }
    }

    documentHasNextPage(doc, currentPage) {
      const wrapper = doc.querySelector('.pagination-wrapper');
      if (!wrapper) return false;

      const current = Number(wrapper.getAttribute('data-page')) || currentPage || 1;
      const links = Array.from(doc.querySelectorAll('.pagination-wrapper a[href]'));

      let maxPage = current;
      links.forEach((link) => {
        const page = this.extractPageNumber(link.getAttribute('href'));
        if (page > maxPage) maxPage = page;
      });

      return maxPage > current;
    }

    getMaxPageFromDocument(doc, fallback = 1) {
      const wrapper = doc.querySelector('.pagination-wrapper');
      if (!wrapper) return fallback;

      const current = Number(wrapper.getAttribute('data-page')) || fallback;
      const links = Array.from(doc.querySelectorAll('.pagination-wrapper a[href]'));
      let maxPage = current;

      links.forEach((link) => {
        const page = this.extractPageNumber(link.getAttribute('href'));
        if (page > maxPage) maxPage = page;
      });

      return maxPage;
    }

    extractPageNumber(href) {
      if (!href) return 1;

      try {
        const url = new URL(href, window.location.origin);
        const value = Number(url.searchParams.get('page'));
        return Number.isFinite(value) && value > 0 ? value : 1;
      } catch (error) {
        return 1;
      }
    }

    extractCardsFromDocument(doc) {
      const nodes = Array.from(doc.querySelectorAll('#product-grid .grid__item'));
      return nodes.map((node) => this.extractCard(node)).filter(Boolean);
    }

    extractCard(cardNode) {
      const rawSkuList = cardNode.getAttribute('data-cic-variant-skus') || '';
      const skuList = rawSkuList
        .split('|')
        .map((sku) => sku.trim())
        .filter(Boolean);

      const title = (
        cardNode.getAttribute('data-cic-title') ||
        cardNode.querySelector('.cic-product-card__title, .full-unstyled-link')?.textContent ||
        ''
      ).trim();

      const vendor = (
        cardNode.getAttribute('data-cic-vendor') ||
        cardNode.querySelector('.cic-product-card__vendor, .card__vendor')?.textContent ||
        ''
      ).trim();

      const productType = (cardNode.getAttribute('data-cic-product-type') || '').trim();
      const available = (cardNode.getAttribute('data-cic-available') || 'true') === 'true';

      return {
        html: cardNode.outerHTML,
        normalizedSkus: skuList.map((sku) => this.normalizeSku(sku)).filter(Boolean),
        titleLower: title.toLowerCase(),
        vendorLower: vendor.toLowerCase(),
        productTypeLower: productType.toLowerCase(),
        available,
      };
    }

    filterCards(cards, rawTerm) {
      const term = rawTerm.trim();
      if (!term) {
        return { mode: '', cards };
      }

      const lowerTerm = term.toLowerCase();
      const keywordTerms = term.toLowerCase().split(/\s+/).filter(Boolean);
      const normalizedExactTerm = this.normalizeExactTerm(term);
      const isSkuQuery = this.isSkuLike(term);
      const normalizedSkuQuery = isSkuQuery ? this.normalizeSku(term) : '';

      const exact = cards.filter((card) => {
        if (isSkuQuery && card.normalizedSkus.some((sku) => sku === normalizedSkuQuery || sku.startsWith(normalizedSkuQuery))) {
          return true;
        }
        return this.matchesExact(card, lowerTerm);
      });
      if (exact.length) {
        return { mode: 'exact', cards: this.withOutOfStockLast(exact) };
      }

      const normalizedExact = cards.filter((card) => this.matchesNormalizedExact(card, normalizedExactTerm));
      if (normalizedExact.length) {
        return { mode: 'normalized_exact', cards: this.withOutOfStockLast(normalizedExact) };
      }

      const keywordFallback = cards.filter((card) => this.matchesKeyword(card, keywordTerms));
      if (keywordFallback.length) {
        return { mode: 'keyword_fallback', cards: this.withOutOfStockLast(keywordFallback) };
      }

      return { mode: 'empty', cards: [] };
    }

    applyFastPreview(rawTerm) {
      const liveCards = this.extractCardsFromDocument(document);
      if (!liveCards.length) return;
      const preview = this.filterCards(liveCards, rawTerm);
      this.renderFilteredGrid(preview.cards);
      this.updateProductCount(preview.cards.length);
      this.applyModeNotice('');
      this.applyMatchedSkuNotes();
    }

    withOutOfStockLast(cards) {
      const inStock = [];
      const outOfStock = [];

      cards.forEach((card) => {
        if (card.available) inStock.push(card);
        else outOfStock.push(card);
      });

      return inStock.concat(outOfStock);
    }

    matchesKeyword(card, terms) {
      if (!terms.length) return true;
      const haystack = `${card.titleLower} ${card.vendorLower} ${card.productTypeLower}`;
      return terms.every((term) => haystack.includes(term));
    }

    matchesExact(card, lowerTerm) {
      if (!lowerTerm) return false;
      const haystack = `${card.titleLower} ${card.vendorLower} ${card.productTypeLower}`;
      return haystack.includes(lowerTerm);
    }

    normalizeExactTerm(value) {
      return (value || '')
        .toLowerCase()
        .replace(/[\s-]+/g, '');
    }

    matchesNormalizedExact(card, normalizedTerm) {
      if (!normalizedTerm) return false;
      const haystack = this.normalizeExactTerm(`${card.titleLower} ${card.vendorLower} ${card.productTypeLower}`);
      return haystack.includes(normalizedTerm);
    }

    renderFilteredGrid(cards) {
      if (!this.productGridContainer) return;

      const pageSize = this.getPageSize();
      const totalPages = Math.max(1, Math.ceil(cards.length / pageSize));
      const requestedPage = this.getCurrentPage();
      const currentPage = Math.min(Math.max(requestedPage, 1), totalPages);
      if (requestedPage !== currentPage) {
        this.replacePageParam(currentPage);
      }

      const pageStart = (currentPage - 1) * pageSize;
      const pageCards = cards.slice(pageStart, pageStart + pageSize);
      const sectionId = this.root.dataset.sectionId || '';

      let html = '';
      if (!cards.length) {
        html = `
          <div class="collection collection--empty page-width" id="product-grid" data-id="${sectionId}">
            <div class="loading-overlay gradient"></div>
            <div class="title-wrapper center">
              <h2 class="title title--primary">No matches in this group of parts.</h2>
            </div>
          </div>
        `;
      } else {
        const collectionClassName = this.initialCollectionClassName || 'collection page-width';
        const gridClassName = this.initialGridClassName || 'grid product-grid';
        const pagination = this.buildPagination(totalPages, currentPage);
        html = `
          <div class="${collectionClassName}">
            <div class="loading-overlay gradient"></div>
            <ul id="product-grid" data-id="${sectionId}" class="${gridClassName}">
              ${pageCards.map((card) => card.html).join('')}
            </ul>
            ${pagination}
          </div>
        `;
      }

      this.isClientRendering = true;
      this.productGridContainer.innerHTML = html;
      this.isClientRendering = false;
      this.ignoreMutationsUntil = Date.now() + 150;
    }

    getPageSize() {
      if (this.initialPageSize && this.initialPageSize > 0) return this.initialPageSize;
      const currentGrid = document.querySelector('#product-grid');
      if (!currentGrid) return 16;
      const count = currentGrid.querySelectorAll('.grid__item').length;
      return count > 0 ? count : 16;
    }

    getCurrentPage() {
      const params = new URLSearchParams(window.location.search);
      const page = Number(params.get('page'));
      if (!Number.isFinite(page) || page < 1) return 1;
      return page;
    }

    replacePageParam(page) {
      const params = new URLSearchParams(window.location.search);
      if (page <= 1) params.delete('page');
      else params.set('page', String(page));
      this.pushUrl(params, true);
    }

    buildPagination(totalPages, currentPage) {
      if (totalPages <= 1) return '';

      const parts = [];
      if (currentPage > 1) {
        parts.push(`
          <li style="flex: 1 0 4.4rem; max-width: 4.4rem;">
            <a href="${this.buildPageUrl(currentPage - 1)}" data-cic-page="${currentPage - 1}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Previous page">&#8249;</a>
          </li>
        `);
      }

      const pageTokens = this.buildPageTokens(totalPages, currentPage);

      pageTokens.forEach((token) => {
        if (token === 'ellipsis') {
          parts.push(`
            <li style="flex: 1 0 4.4rem; max-width: 4.4rem;">
              <span class="pagination__item" aria-hidden="true">…</span>
            </li>
          `);
          return;
        }

        const page = token;
        if (page === currentPage) {
          parts.push(`
            <li style="flex: 1 0 4.4rem; max-width: 4.4rem;">
              <a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ${page}">
                ${page}
              </a>
            </li>
          `);
          return;
        }

        parts.push(`
            <li style="flex: 1 0 4.4rem; max-width: 4.4rem;">
              <a href="${this.buildPageUrl(page)}" data-cic-page="${page}" class="pagination__item link" aria-label="Page ${page}">${page}</a>
            </li>
          `);
      });

      if (currentPage < totalPages) {
        parts.push(`
          <li style="flex: 1 0 4.4rem; max-width: 4.4rem;">
            <a href="${this.buildPageUrl(currentPage + 1)}" data-cic-page="${currentPage + 1}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Next page">&#8250;</a>
          </li>
        `);
      }

      return `
        <div class="pagination-wrapper cic-quick-pagination" data-page="${currentPage}">
          <nav class="pagination" role="navigation" aria-label="Pagination">
            <ul class="pagination__list list-unstyled" role="list" style="display: flex; flex-wrap: wrap; justify-content: center;">
              ${parts.join('')}
            </ul>
          </nav>
        </div>
      `;
    }

    buildPageTokens(totalPages, currentPage) {
      if (totalPages <= 7) {
        const allPages = [];
        for (let page = 1; page <= totalPages; page += 1) allPages.push(page);
        return allPages;
      }

      const pages = new Set([1, totalPages]);
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let page = start; page <= end; page += 1) pages.add(page);

      if (currentPage <= 3) {
        pages.add(2);
        pages.add(3);
        pages.add(4);
      }

      if (currentPage >= totalPages - 2) {
        pages.add(totalPages - 1);
        pages.add(totalPages - 2);
        pages.add(totalPages - 3);
      }

      const sortedPages = Array.from(pages)
        .filter((page) => page >= 1 && page <= totalPages)
        .sort((a, b) => a - b);

      const tokens = [];
      for (let index = 0; index < sortedPages.length; index += 1) {
        const current = sortedPages[index];
        const previous = sortedPages[index - 1];
        if (index > 0 && current - previous > 1) tokens.push('ellipsis');
        tokens.push(current);
      }

      return tokens;
    }

    buildPageUrl(page) {
      const params = new URLSearchParams(window.location.search);
      if (page <= 1) params.delete('page');
      else params.set('page', String(page));
      const queryString = params.toString();
      return `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
    }

    updateProductCount(count) {
      const text = `${count} ${count === 1 ? 'product' : 'products'}`;
      const productCount = document.getElementById('ProductCount');
      if (productCount) productCount.textContent = text;
      const productCountDesktop = document.getElementById('ProductCountDesktop');
      if (productCountDesktop) productCountDesktop.textContent = text;
    }

    writeModeToUrl(mode, term) {
      const params = new URLSearchParams(window.location.search);
      let changed = false;

      const currentMode = params.get('cic_q_mode') || '';
      if (mode && currentMode !== mode) {
        params.set('cic_q_mode', mode);
        changed = true;
      } else if (!mode && currentMode) {
        params.delete('cic_q_mode');
        changed = true;
      }

      const currentDisplay = params.get('cic_q_display') || '';
      if (term && currentDisplay !== term) {
        params.set('cic_q_display', term);
        changed = true;
      } else if (!term && currentDisplay) {
        params.delete('cic_q_display');
        changed = true;
      }

      if (!changed) return;
      this.pushUrl(params, true);
    }

    applyMatchedSkuNotes() {
      const cards = document.querySelectorAll('#product-grid .grid__item[data-cic-variant-skus]');
      if (!cards.length) return;

      const params = new URLSearchParams(window.location.search);
      const mode = params.get('cic_q_mode') || '';
      const displayValue = params.get('cic_q_display') || this.input?.value || '';
      const normalizedQuery = this.normalizeSku(displayValue);
      const skuMode = mode === 'exact' || mode === 'normalized_exact';

      cards.forEach((card) => {
        const existing = card.querySelector('.cic-matched-sku');
        if (existing) existing.remove();
        if (!skuMode || !normalizedQuery) return;

        const rawSkuList = card.getAttribute('data-cic-variant-skus') || '';
        const skuList = rawSkuList.split('|').map((sku) => sku.trim()).filter(Boolean);
        if (!skuList.length) return;

        const matchedSku = skuList.find((sku) => {
          const normalizedSku = this.normalizeSku(sku);
          if (mode === 'exact') return normalizedSku === normalizedQuery || normalizedSku.startsWith(normalizedQuery);
          return normalizedSku.includes(normalizedQuery);
        });

        if (!matchedSku) return;
        const host = card.querySelector('.cic-product-card__body, .card__information');
        if (!host) return;

        const note = document.createElement('p');
        note.className = 'cic-matched-sku';
        note.textContent = `Matched SKU: ${matchedSku}`;
        host.appendChild(note);
      });
    }

    toggleClearButton() {
      if (!this.clearButton || !this.input) return;
      this.clearButton.hidden = this.input.value.trim().length === 0;
    }

    isSkuLike(value) {
      if (!value) return false;
      const candidate = value.trim();
      return /^[A-Za-z0-9][A-Za-z0-9\s-]*$/.test(candidate) && /\d/.test(candidate);
    }

    normalizeSku(value) {
      return value.toUpperCase().replace(/[\s-]+/g, '');
    }
  }

  function initQuickSearchControllers(root = document) {
    const hosts = root.querySelectorAll('cic-collection-quick-search');
    hosts.forEach((host) => {
      if (host.__cicQuickController) return;
      const controller = new CicCollectionQuickSearchController(host);
      host.__cicQuickController = controller;
      controller.connect();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initQuickSearchControllers(document));
  } else {
    initQuickSearchControllers(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target || document;
    initQuickSearchControllers(section);
  });
})();
